import { type AssistantSkill, type FredFeedbackItem, SETTING_DEFAULTS } from "@nmt/shared";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { errorMessage, get, post } from "../../api";
import { Alert, Button, Checkbox, Field, Loading, PageHeader, Panel, PanelHeader, TextAreaField } from "../../components/ui";
import { useSettings } from "./useSettings";

type Keys = "ai.receiptPrompt" | "ai.measurementPrompt" | "photos.aiPrompt" | "ai.fredPrompt";

/** Settings → AI: what the readers are told, and AI photo tagging. All on Amazon Bedrock; nothing leaves AWS. */
export function AiSettings() {
  const { query, save } = useSettings();
  const [v, setV] = useState<Record<Keys, string> | null>(null);
  const [tagging, setTagging] = useState(false);
  const [fredLimit, setFredLimit] = useState("300");
  const [notes, setNotes] = useState<string[] | null>(null);
  const [skills, setSkills] = useState<AssistantSkill[] | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  useEffect(() => {
    const s = query.data?.settings;
    if (s && !v) {
      setV({ "ai.receiptPrompt": s["ai.receiptPrompt"], "ai.measurementPrompt": s["ai.measurementPrompt"], "photos.aiPrompt": s["photos.aiPrompt"], "ai.fredPrompt": s["ai.fredPrompt"] });
      setTagging(s["photos.aiTagging"]);
      setFredLimit(String(s["ai.fredDailyLimit"]));
      setNotes(s["ai.fredNotes"]);
      setSkills(s["ai.assistantSkills"]);
    }
  }, [query.data, v]);
  if (query.isLoading || !v || !notes || !skills) return <Loading />;
  const prompt = (key: Keys, title: string, description: string, extra?: React.ReactNode) => (
    <Panel>
      <PanelHeader
        title={title}
        description={description}
        action={
          v[key] !== SETTING_DEFAULTS[key] && (
            <Button variant="quiet" className="px-3 py-1.5" onClick={() => setV({ ...v, [key]: SETTING_DEFAULTS[key] })}>
              Back to the original
            </Button>
          )
        }
      />
      <div className="flex flex-col gap-3 px-5 py-4">
        {extra}
        <TextAreaField label="Instructions" rows={key === "ai.fredPrompt" ? 12 : 6} maxLength={key === "ai.fredPrompt" ? 6000 : 4000} value={v[key]} onChange={(e) => setV({ ...v, [key]: e.target.value })} />
      </div>
    </Panel>
  );
  return (
    <div className="flex flex-col gap-5">
      <PageHeader title="AI" description="What the readers are told. Changes apply to the next receipt, report, or photo; check a few after changing them. The insurance estimate reader is the one from the proposal Worker and isn't changed here." />
      {save.error && <Alert>{errorMessage(save.error)}</Alert>}
      {notice && <Alert tone="ok">{notice}</Alert>}
      {prompt("ai.receiptPrompt", "Receipts and bills", "Reads vendor, date, amounts, and category from receipt photos and PDFs.")}
      {prompt("ai.measurementPrompt", "Measurement reports", "Reads area, pitches, and lengths from Roofr, Hover, and EagleView PDFs.")}
      {prompt(
        "ai.fredPrompt",
        "Fred, the assistant",
        "How Fred works and talks. He can only do what the person asking can do, and always asks before changing anything; that part isn't up to these instructions.",
        <Field label="Messages each person can send Fred a day" type="number" min={0} max={5000} value={fredLimit} onChange={(e) => setFredLimit(e.target.value)} className="max-w-xs" />,
      )}
      <FredNotes notes={notes} onChange={setNotes} />
      <AssistantSkills skills={skills} onChange={setSkills} />
      <FredFeedback
        onAddNote={(n) => {
          if (notes.includes(n) || notes.length >= 100) return;
          const next = [...notes, n];
          setNotes(next);
          // Saved straight away (the rest of the page still waits for Save).
          save.mutate({ "ai.fredNotes": next }, { onSuccess: () => setNotice("Note added to Fred's notes.") });
        }}
      />
      {prompt("photos.aiPrompt", "Photo tagging", "Picks tags from the photo-tag list (Settings → Lists) and writes a caption.", <Checkbox label="Tag new photos automatically" checked={tagging} onChange={setTagging} />)}
      <div className="flex justify-end">
        <Button variant="primary" busy={save.isPending} onClick={() => (setNotice(null), save.mutate({ ...v, "photos.aiTagging": tagging, "ai.fredDailyLimit": Math.max(0, Math.round(Number(fredLimit) || 0)), "ai.fredNotes": notes.map((n) => n.trim()).filter(Boolean), "ai.assistantSkills": skills.map((k) => ({ name: k.name.trim(), instructions: k.instructions.trim() })).filter((k) => k.name && k.instructions) }, { onSuccess: () => setNotice("Saved.") }))}>
          Save
        </Button>
      </div>
    </div>
  );
}

/** Things Fred should know: short notes added to his instructions on every message. Fred can also suggest them. */
function FredNotes({ notes, onChange }: { notes: string[]; onChange: (notes: string[]) => void }) {
  const [draft, setDraft] = useState("");
  const add = () => {
    const n = draft.trim();
    if (!n || notes.includes(n) || notes.length >= 100) return;
    onChange([...notes, n]);
    setDraft("");
  };
  return (
    <Panel>
      <PanelHeader
        title="Things Fred should know"
        description="Facts and rules about how the company works, like who owns a property, which workflow a kind of job goes in, or who handles an area. Fred reads them on every message. He'll offer to add one when you tell him to remember something. Save at the bottom."
      />
      <div className="flex flex-col gap-2 px-5 py-4">
        {!notes.length && <div className="text-sm text-muted">No notes yet.</div>}
        {notes.map((n, i) => (
          <div key={i} className="flex items-start gap-2">
            <textarea
              className="min-h-10 min-w-0 flex-1 resize-y rounded-[var(--radius-control)] border border-line bg-white px-3 py-2 text-[15px] focus:border-brand focus:outline-none"
              rows={1}
              maxLength={500}
              value={n}
              onChange={(e) => onChange(notes.map((x, j) => (j === i ? e.target.value : x)))}
              aria-label={`Note ${i + 1}`}
            />
            <Button variant="quiet" className="px-2 py-2" aria-label={`Remove note ${i + 1}`} onClick={() => onChange(notes.filter((_, j) => j !== i))}>
              Remove
            </Button>
          </div>
        ))}
        <div className="flex gap-2">
          <Field label="Add a note" className="min-w-0 flex-1" maxLength={500} value={draft} placeholder="e.g. Commercial jobs start in Commercial → Lead." onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add())} />
          <Button className="self-end" disabled={!draft.trim() || notes.length >= 100} onClick={add}>
            Add
          </Button>
        </div>
      </div>
    </Panel>
  );
}

/** The personal assistant's skills: what Fred does with what comes in on people's own texts, in plain words. */
function AssistantSkills({ skills, onChange }: { skills: AssistantSkill[]; onChange: (skills: AssistantSkill[]) => void }) {
  const set = (i: number, k: Partial<AssistantSkill>) => onChange(skills.map((x, j) => (j === i ? { ...x, ...k } : x)));
  return (
    <Panel>
      <PanelHeader
        title="Assistant skills"
        description="How Fred handles what comes in on people's own texts (the Assistant page). Write each one the way you'd explain it to a new assistant. Everyone's Fred uses these; what he knows about each person stays theirs. Save at the bottom."
        action={
          JSON.stringify(skills) !== JSON.stringify(SETTING_DEFAULTS["ai.assistantSkills"]) && (
            <Button variant="quiet" className="px-3 py-1.5" onClick={() => onChange(SETTING_DEFAULTS["ai.assistantSkills"])}>
              Back to the original
            </Button>
          )
        }
      />
      <div className="flex flex-col gap-4 px-5 py-4">
        {skills.map((k, i) => (
          <div key={i} className="flex flex-col gap-2 rounded-[var(--radius-control)] border border-line p-3">
            <div className="flex items-end gap-2">
              <Field label="Skill" className="min-w-0 flex-1" maxLength={80} value={k.name} onChange={(e) => set(i, { name: e.target.value })} />
              <Button variant="quiet" className="px-2 py-2" onClick={() => onChange(skills.filter((_, j) => j !== i))}>
                Remove
              </Button>
            </div>
            <TextAreaField label="What Fred does" rows={8} maxLength={3000} value={k.instructions} onChange={(e) => set(i, { instructions: e.target.value })} />
          </div>
        ))}
        <div>
          <Button disabled={skills.length >= 30} onClick={() => onChange([...skills, { name: "", instructions: "" }])}>
            Add a skill
          </Button>
        </div>
      </div>
    </Panel>
  );
}

/** What people flagged: thumbs-down on Fred's answers, and notes they suggested. */
function FredFeedback({ onAddNote }: { onAddNote: (note: string) => void }) {
  const feedback = useQuery({ queryKey: ["fred", "feedback"], queryFn: () => get<{ items: FredFeedbackItem[]; upCount: number }>("/api/fred/feedback") });
  const done = async (id: string) => {
    await post(`/api/fred/feedback/${id}/done`);
    void feedback.refetch();
  };
  const items = feedback.data?.items ?? [];
  return (
    <Panel>
      <PanelHeader
        title="Fred feedback"
        description={`Answers people marked as not right, and notes they suggested. Turn them into notes or instruction changes, then mark them done.${feedback.data ? ` ${feedback.data.upCount} thumbs up so far.` : ""}`}
      />
      <div className="flex flex-col divide-y divide-mist">
        {feedback.isLoading && <div className="px-5 py-4 text-sm text-muted">Loading…</div>}
        {!feedback.isLoading && !items.length && <div className="px-5 py-4 text-sm text-muted">Nothing to look at.</div>}
        {items.map((f) => (
          <div key={f.id} className="flex flex-col gap-1.5 px-5 py-3 text-sm">
            <div className="text-xs text-muted">
              {f.kind === "note" ? "Suggested note" : "Not right"} · {f.userName} · {new Date(f.createdAt).toLocaleString()}
            </div>
            {f.kind === "note" ? (
              <div className="font-medium text-ink">{f.comment}</div>
            ) : (
              <>
                {f.question && (
                  <div>
                    <span className="font-semibold">Asked:</span> {f.question}
                  </div>
                )}
                <div className="max-h-32 overflow-y-auto rounded-md bg-mist px-2 py-1 whitespace-pre-wrap text-ink-soft">{f.answer || "(no text)"}</div>
                {f.comment && (
                  <div>
                    <span className="font-semibold">Should have:</span> {f.comment}
                  </div>
                )}
              </>
            )}
            <div className="flex gap-2">
              {f.kind === "note" && (
                <Button variant="primary" className="px-3 py-1" onClick={() => (onAddNote(f.comment), void done(f.id))}>
                  Add to notes
                </Button>
              )}
              <Button className="px-3 py-1" onClick={() => void done(f.id)}>
                {f.kind === "note" ? "Dismiss" : "Done"}
              </Button>
            </div>
          </div>
        ))}
      </div>
    </Panel>
  );
}
